import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import OtpInput from '../components/common/OtpInput';
import DatePicker from '../components/common/DatePicker';
import { useRequestOtpMutation, useSignupMutation } from '../store/authApi';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';

// Patient self-signup form. user_type is fixed to "patient" — no UI
// for it. Other roles (doctor, lab partner, organisation) are
// onboarded internally by the admin team and never come through this
// page.
//
// Two-step flow:
//   1. Details — Full Name, Mobile, DOB, Gender. On submit we call
//      /auth/request-otp to ship a 6-digit code to the phone.
//   2. OTP — user enters the code; we call /auth/signup with the
//      full payload + otp. Server re-verifies the OTP (defence in
//      depth) and creates the account.
const GENDER_OPTIONS = [
  { value: 'male',   label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other',  label: 'Other' },
];

const Signup = () => {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const { showToast } = useUI();

  const [step, setStep] = useState('details'); // 'details' | 'otp'
  const [form, setForm] = useState({
    name: '',
    phone: '',
    dob: '',
    gender: '',
  });
  const [otp, setOtp] = useState('');
  const [errMsg, setErrMsg] = useState('');

  const [requestOtp, { isLoading: sending }] = useRequestOtpMutation();
  const [signup,     { isLoading: creating }] = useSignupMutation();

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Phone is 10 digits in state; prepend +91 only when calling the API.
  const fullPhone = `+91${form.phone}`;

  // ── Step 1: validate + request OTP ──
  const onSendOtp = async (e) => {
    e?.preventDefault?.();
    setErrMsg('');
    if (!form.name.trim())             return setErrMsg('Please enter your full name');
    if (!/^\d{10}$/.test(form.phone))  return setErrMsg('Enter a valid 10-digit mobile number');
    if (!form.dob)                     return setErrMsg('Please enter your date of birth');
    if (!form.gender)                  return setErrMsg('Please select your gender');

    try {
      await requestOtp({ phone: fullPhone }).unwrap();
      setStep('otp');
      showToast('OTP sent — check your phone');
    } catch (err) {
      setErrMsg(err?.data?.message || 'Failed to send OTP. Please try again.');
    }
  };

  // ── Step 2: submit signup with OTP ──
  const onCreateAccount = async (e) => {
    e?.preventDefault?.();
    setErrMsg('');
    if (!/^\d{6}$/.test(otp.trim())) return setErrMsg('Enter the 6-digit OTP');

    try {
      const res = await signup({
        name: form.name.trim(),
        phone: fullPhone,
        otp: otp.trim(),
        dob: form.dob,
        gender: form.gender,
        user_type: 'patient',
      }).unwrap();

      // Signup is OTP-based, not credentials-based — leave the
      // /reports UHID gate enabled until the user goes through
      // verify-credentials at least once.
      setSession({ token: res.token, user: res.user, uhidVerified: false });
      showToast(`Welcome to TrustLab, ${res.user.name}!`);
      navigate(ROUTES.HOME, { replace: true });
    } catch (err) {
      setErrMsg(err?.data?.message || 'Could not create account. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        bgcolor: 'var(--cream)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 5, md: 7 },
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 460 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid var(--border-l)',
            borderRadius: '20px',
            p: { xs: 3.25, md: 4.5 },
            boxShadow: '0 12px 40px rgba(21,42,30,0.06)',
          }}
        >
          <Typography
            className="serif"
            sx={{
              fontSize: { xs: 26, md: 32 },
              fontWeight: 600,
              color: 'var(--ink)',
              mb: 0.75,
              lineHeight: 1.1,
            }}
          >
            {step === 'details' ? 'Create your account' : 'Verify your phone'}
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'var(--stone)', mb: 3.5, fontWeight: 300 }}>
            {step === 'details'
              ? "A few details to get started — we'll send a one-time password to verify your mobile number."
              : `We've sent a 6-digit OTP to +91 ${form.phone}.`}
          </Typography>

          {errMsg && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: '12px' }}>
              {errMsg}
            </Alert>
          )}

          {/* ── Step 1: details form ── */}
          {step === 'details' && (
            <Box component="form" onSubmit={onSendOtp}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Full name"
                    value={form.name}
                    onChange={onChange('name')}
                    disabled={sending}
                    autoFocus
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Mobile number"
                    placeholder="9876543210"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      phone: e.target.value.replace(/\D/g, '').slice(0, 10),
                    }))}
                    disabled={sending}
                    inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="span"
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: 'var(--ink)',
                              pr: 1,
                              mr: 1,
                              borderRight: '1px solid var(--border-l)',
                              lineHeight: 1,
                            }}
                          >
                            +91
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Date of birth"
                    value={form.dob}
                    onChange={(iso) => setForm((f) => ({ ...f, dob: iso }))}
                    disabled={sending}
                    // Cap at today so DOB can't be in the future.
                    maxDate={new Date().toISOString().slice(0, 10)}
                    // Min ~120 years ago — generous floor for the year picker.
                    minDate={`${new Date().getFullYear() - 120}-01-01`}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Gender"
                    value={form.gender}
                    onChange={onChange('gender')}
                    disabled={sending}
                  >
                    {GENDER_OPTIONS.map((g) => (
                      <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={sending}
                sx={{
                  mt: 3,
                  borderRadius: 100,
                  py: 1.25,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: 15,
                }}
              >
                {sending ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send OTP'}
              </Button>
            </Box>
          )}

          {/* ── Step 2: OTP entry ── */}
          {step === 'otp' && (
            <Box component="form" onSubmit={onCreateAccount}>
              <Box sx={{ mb: 2.5 }}>
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  disabled={creating}
                  autoFocus
                />
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={creating}
                sx={{
                  borderRadius: 100,
                  py: 1.25,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: 15,
                  mb: 1.5,
                }}
              >
                {creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Verify & Create Account'}
              </Button>
              <Button
                fullWidth
                onClick={() => { setStep('details'); setOtp(''); setErrMsg(''); }}
                sx={{ textTransform: 'none', fontSize: 13, color: 'var(--stone)' }}
              >
                ← Edit details
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 3, textAlign: 'center', borderTop: '1px solid var(--border-l)', pt: 2.5 }}>
            <Typography sx={{ fontSize: 13, color: 'var(--stone)' }}>
              Already have an account?{' '}
              <Box
                component={Link}
                to={ROUTES.LOGIN}
                sx={{
                  color: 'var(--sage)',
                  fontWeight: 700,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Sign in
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Signup;
